"use client";

import { CircleCheck, TriangleAlert } from "lucide-react";
import Link from "next/link";

import { cn } from "@/lib/utils";

import { BACK_TO_LOGIN } from "@/features/auth/components/auth-request-content";

/* --- Notice ---------------------------------------------------------------- */

/**
 * The status notice shared by /forgot, /signup/resend_activation and
 * /reset_password. Live renders a bootstrap alert with a leading glyph — a check
 * for the success, a cross for the failures — so the shape is kept and only the
 * skin changes. Solid-tinted surfaces with a light foreground, never the
 * low-alpha status-token chips that fail AA on this background (see the
 * tinted-token contrast note).
 *
 * Takes a `tone` rather than a `RequestState` because /reset_password is not an
 * email-request page: it has no `sent` state and every notice it shows is an
 * error. The two request pages map their state to a tone at the call site.
 */
export function RequestNotice({
  tone,
  children,
  className,
}: {
  tone: "success" | "error";
  children: React.ReactNode;
  className?: string;
}) {
  const ok = tone === "success";
  const Icon = ok ? CircleCheck : TriangleAlert;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-start gap-3 rounded-xl border px-4 py-3.5 text-[15px] leading-relaxed",
        ok
          ? "border-success/25 bg-success/10 text-success-text"
          : "border-destructive/25 bg-destructive/10 text-destructive-text",
        className,
      )}
    >
      <Icon
        aria-hidden="true"
        className={cn(
          "mt-0.5 size-[18px] shrink-0",
          ok ? "text-success-text" : "text-destructive-text",
        )}
      />
      <span>{children}</span>
    </div>
  );
}

/* --- Back to login --------------------------------------------------------- */

/** Live swaps the whole form for this link once the email is away. */
export function BackToLogin({ className }: { className?: string }) {
  return (
    <Link
      href="/login"
      className={cn(
        "inline-flex h-11 items-center gap-2 rounded-xl border border-border px-5 text-sm font-medium text-muted-foreground",
        "transition-colors duration-150 hover:border-border hover:text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className,
      )}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="size-3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M15 18l-6-6 6-6" />
      </svg>
      {BACK_TO_LOGIN}
    </Link>
  );
}

/* --- Field + button -------------------------------------------------------- */

/** Shared input skin. No resting ring or glow — emphasis only on focus. */
export const FIELD_BASE =
  "h-12 w-full rounded-xl border border-input bg-elevated px-4 text-[15px] text-foreground placeholder:text-muted-foreground " +
  "transition-colors duration-150 hover:border-border " +
  "focus:outline-none focus:border-[hsl(var(--primary))] focus:ring-1 focus:ring-[hsl(var(--primary))] " +
  "disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Shared CTA skin. Live's button is `#00a651` green; restyled onto the theme's
 * brand accent (`--primary`, Polymarket #0093fd in dark). Flat at rest — the
 * only emphasis is the hover shift and the focus ring.
 */
export const CTA_BASE =
  "inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[hsl(var(--primary))] px-6 text-[15px] font-semibold text-[hsl(var(--primary-foreground))] " +
  "transition-colors duration-150 hover:bg-[hsl(var(--primary)/0.85)] active:bg-[hsl(var(--primary)/0.9)] " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
  "disabled:cursor-not-allowed disabled:opacity-60";

/** The button's busy glyph. */
export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={cn("size-4 animate-spin", className)}
      fill="none"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="2.5"
        opacity="0.25"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
